
/**
 * Yüzde hesaplar.
 * @param {number} part - Bölünen sayı
 * @param {number} total - Bölünen sayı
 * @returns {number} - Yüzde
 * yuzdeHesapla(50, 100) // 50
 * yuzdeHesapla(25, 100) // 25
 */
export const yuzdeHesapla = (part, total, precision = 2) => {
  if (total === 0) {
    return 0
  }
  
  const percentage = (part / total) * 100
  
  return parseFloat(percentage.toFixed(precision))
}

/**
 * Sayıları güvenli bir şekilde float'a çevirir.
 * @param {number} value - Formatlanacak sayı
 * @returns {number} - Sayı
 * safeParseFloat(1250.5) // 1250.5
 * safeParseFloat(null) // 0
 * safeParseFloat(undefined) // 0
 * safeParseFloat(NaN) // 0
 */
export const safeParseFloat = value => {
  if (value === null || value === undefined || isNaN(value)) return 0
  const parsed = parseFloat(value)
  
  return isNaN(parsed) ? 0 : parsed
}

/**
 * Para birimini yerel ayarlara göre formatlar.
 * @param {number} value - Formatlanacak sayı
 * @param {string} locale - Dil kodu (Varsayılan: 'tr')
 * @param {string} currency - Para birimi kodu (Varsayılan: 'TRY')
 * @returns {string} - Formatlanmış sayı
 * formatCurrencyI18n(1250.5) // ₺1.250,50
 * formatCurrencyI18n(1250.5, 'en') // $1,250.50
 * formatCurrencyI18n(1250.5, 'nl') // €1.250,50
 */
export const formatCurrencyI18n = (value, locale = 'tr', currency = 'TRY') => {
  if (value === null || value === undefined) return '-'
  
  const localeMap = {
    'tr': 'tr-TR',
    'en': 'en-US',
    'nl': 'nl-NL',
  }

  const mappedLocale = localeMap[locale] || locale || 'tr-TR'

  return new Intl.NumberFormat(mappedLocale, {
    style: 'currency',
    currency: currency,
  }).format(value)
}

/**
 * Para birimini yerel ayarlara göre formatlar.
 * @param {number} value - Formatlanacak sayı
 * @param {string} currency - Para birimi kodu (Varsayılan: 'TRY')
 * @returns {string} - Formatlanmış sayı
 * formatCurrency(1250.5) // ₺1.250,50
 */
export const formatCurrency = (value, currency = 'TRY') => {
  return formatNumber(value, 2, 2, "currency", currency)
}

/**
 * Sayıları yerel ayarlara göre formatlar.
 * @param {number} value - Formatlanacak sayı
 * @param {number} minFraction - Minimum ondalık basamak sayısı (Varsayılan: 0)
 * @param {number} maxFraction - Maksimum ondalık basamak sayısı (Varsayılan: 2)
 * @param {string} style - Format stili ('decimal', 'currency', 'percent')
 * @param {string} currency - Para birimi kodu (Eğer style 'currency' ise kullanılır)
 * @returns {string} - Formatlanmış sayı
 * Tam Sayı	formatNumber(1250.5, 0, 0)	1.251 (Yuvarlar)
 * Standart Ondalık	formatNumber(1250.5, 0, 2)	1.250,5
 * Para Birimi (TL)	formatNumber(1250.5, 2, 2, "currency")	₺1.250,50
 * Yüzde Formatı	formatNumber(0.255, 1, 1, "percent")	%25,5
 */
export const formatNumber = (
  value,
  minFraction = 0,
  maxFraction = 2,
  style = "decimal",
  currency = "TRY",
) => {
  return new Intl.NumberFormat("tr-TR", {
    style: style,
    currency: currency,
    minimumFractionDigits: minFraction,
    maximumFractionDigits: maxFraction,
  }).format(value)
}
export const numberToTurkishText = (num, currency = "TL", subCurrency = "Kuruş") => {
  const digits = ["", "Bir", "İki", "Üç", "Dört", "Beş", "Altı", "Yedi", "Sekiz", "Dokuz"]
  const tens = ["", "On", "Yirmi", "Otuz", "Kırk", "Elli", "Atmış", "Yetmiş", "Seksen", "Doksan"]
  const units = ["", "Bin", "Milyon", "Milyar", "Trilyon", "Katrilyon"]

  const convertThreeDigit = n => {
    let res = ""
    const h = Math.floor(n / 100)
    const t = Math.floor((n % 100) / 10)
    const o = n % 10

    if (h > 0) {
      if (h > 1) res += digits[h]
      res += "Yüz"
    }
    res += tens[t]
    res += digits[o]
    
    return res
  }

  const convertPart = n => {
    if (n === 0) return ""
    let res = ""
    let unitIdx = 0
    let temp = n

    while (temp > 0) {
      const part = temp % 1000
      if (part > 0) {
        let partStr = convertThreeDigit(part)
        if (unitIdx === 1 && part === 1) partStr = "" // special case for "Bin" (not "Bir Bin")
        res = partStr + units[unitIdx] + res
      }
      temp = Math.floor(temp / 1000)
      unitIdx++
    }
    
    return res
  }

  const mainPart = Math.floor(num)
  const decimalPart = Math.round((num - mainPart) * 100)

  // Otomatik para birimi eşleştirme
  const currencyMapping = {
    'TRY': { main: 'TL', sub: 'Kuruş' },
    'USD': { main: 'Dolar', sub: 'Cent' },
    'EUR': { main: 'Euro', sub: 'Cent' },
    'GBP': { main: 'Sterlin', sub: 'Pence' },
  }

  const selectedCurrency = currencyMapping[currency.toUpperCase()] || { main: currency, sub: subCurrency }
  const finalCurrency = selectedCurrency.main
  const finalSubCurrency = selectedCurrency.sub

  let result = "Yalnız "
  if (mainPart === 0) result += "Sıfır"
  else result += convertPart(mainPart)
  
  result += " " + finalCurrency

  if (decimalPart > 0) {
    result += ", " + convertThreeDigit(decimalPart) + " " + finalSubCurrency
  }

  return result
}
